import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Bell, BellRing } from "lucide-react";

function timeAgo(iso, lang) {
  const diff = (Date.now() - new Date(iso).getTime()) / 6e4;
  if (diff < 1) return lang === "fr" ? "à l'instant" : "just now";
  if (diff < 60) return `${Math.floor(diff)} min`;
  if (diff < 1440) return `${Math.floor(diff / 60)} h`;
  return `${Math.floor(diff / 1440)} ${lang === "fr" ? "j" : "d"}`;
}

export const NotificationsBell = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data.notifications);
      setUnread(r.data.unread);
    } catch (e) { console.error("Notifications load error:", e); }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [user, load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openPanel = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read"); setUnread(0); }
      catch (e) { console.error("Mark notifications read error:", e); }
    }
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notifications-bell-btn"
        onClick={openPanel}
        className="relative grid place-items-center h-9 w-9 rounded-full border border-border hover:bg-secondary transition-colors"
      >
        {unread > 0 ? <BellRing className="h-4 w-4 text-primary" /> : <Bell className="h-4 w-4" />}
        {unread > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-1 -right-1 grid place-items-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto rounded-2xl border border-border bg-card shadow-xl z-50">
          <div className="px-4 py-3 border-b border-border font-display font-semibold text-sm">
            {lang === "fr" ? "Notifications" : "Notifications"}
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
              {lang === "fr" ? "Aucune notification" : "No notifications"}
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                data-testid={`notification-${n.id}`}
                onClick={() => { setOpen(false); navigate("/feed"); }}
                className="w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary transition-colors flex gap-3"
              >
                <div className={`mt-0.5 grid place-items-center h-8 w-8 rounded-full shrink-0 ${n.read ? "bg-secondary text-muted-foreground" : "bg-primary/10 text-primary"}`}>
                  <Bell className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{n.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{n.body}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.created_at, lang)}</p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
